import { computeBreakeven, type LeverResult } from '../lib/breakeven';
import { formatMoney } from '../lib/currency';
import type { EventData, VatMode } from '../lib/types';

type Props = {
  data: EventData;
  currency: string;
  vatMode: VatMode;
};

export default function BreakevenCard({ data, currency, vatMode }: Props) {
  const b = computeBreakeven(data);

  return (
    <div className="card breakeven-card">
      <div className="section-head">
        <h2>Break-even</h2>
        <span className="muted">
          Holding the other revenue streams at their current values · amounts {vatMode === 'net' ? 'net of VAT' : 'gross'}.
        </span>
      </div>

      <div className="be-grid">
        <div className="be-corner" />
        <div className="be-col-head">At min prices</div>
        <div className="be-col-head">At max prices</div>

        <div className="be-row-head">Tickets to sell</div>
        <CountCell lever={b.min.tickets} />
        <CountCell lever={b.max.tickets} />

        <div className="be-row-head">Sponsors to land</div>
        <CountCell lever={b.min.sponsors} />
        <CountCell lever={b.max.sponsors} />

        <div className="be-row-head">Cost reduction needed</div>
        <CostDownCell amount={b.min.costDown.needed} cleared={b.min.costDown.cleared} currency={currency} />
        <CostDownCell amount={b.max.costDown.needed} cleared={b.max.costDown.cleared} currency={currency} />
      </div>

      <div className="be-foot muted">
        Each lever is independent: it answers “how much of THIS one alone would
        cover the gap, leaving the other two as you've planned them.”
      </div>
    </div>
  );
}

function CountCell({ lever }: { lever: LeverResult }) {
  if (lever.cleared) {
    return (
      <div className="be-cell good">
        <span className="be-primary">Cleared ✓</span>
        <span className="be-secondary">others already cover costs</span>
      </div>
    );
  }
  if (!lever.computable) {
    return (
      <div className="be-cell empty">
        <span className="be-primary">—</span>
        <span className="be-secondary">no qty entered</span>
      </div>
    );
  }
  const overPlan = Number.isFinite(lever.pctOfPlan) && lever.pctOfPlan > 1;
  const tone = overPlan ? 'bad' : 'good';
  const pct = Number.isFinite(lever.pctOfPlan)
    ? `${Math.round(lever.pctOfPlan * 100)}% of plan`
    : '—';
  return (
    <div className={`be-cell ${tone}`}>
      <span className="be-primary">
        {formatCount(lever.needed)}
        {lever.planned > 0 && (
          <span className="be-of"> / {formatCount(lever.planned)}</span>
        )}
      </span>
      <span className="be-secondary">{pct}</span>
    </div>
  );
}

function CostDownCell({
  amount,
  cleared,
  currency,
}: {
  amount: number;
  cleared: boolean;
  currency: string;
}) {
  if (cleared) {
    return (
      <div className="be-cell good">
        <span className="be-primary">None</span>
        <span className="be-secondary">already covered</span>
      </div>
    );
  }
  return (
    <div className="be-cell bad">
      <span className="be-primary">{formatMoney(amount, currency)}</span>
      <span className="be-secondary">to remove from costs</span>
    </div>
  );
}

function formatCount(n: number): string {
  return n.toLocaleString('en-US').replace(/,/g, ' ');
}

